import Axios from "../config/axios-v1-config";

// Holdings del operador (microservice-contracts, controller `contracts/holdings`). Es el
// dueño de la colección `holdings`: identidad fiscal y membresía de sus empresas. Solo
// staff del operador (admin, superadmin logístico, supervisor, soporte): contracts responde
// 403 `forbidden_role` a otro rol y 403 `operator_required` si el token no trae operador. El
// operador sale SIEMPRE del token: ningún body lleva `logisticCompanyId`.
//
// No confundir con `api/holding.js`, que es el cliente del PORTAL del holding (rol holding).
//
// Errores propios: { statusCode, code, message }. Los 400 de validación son los de Nest:
// { statusCode: 400, message: string[], error: 'Bad Request' }.
const HOLDINGS = "contracts/holdings";

// Holdings del operador ordenados por nombre, sin paginar:
// [{ id, name, legalName, businessIdentifier, activity,
//    fiscalAddress: { street, municipality, city, state, country }, missingFiscalFields,
//    companies: [{ id, name, legalName, businessIdentifier, source: 'list'|'holdingId'|'both' }],
//    inconsistencies: { missingInList, missingHoldingId, danglingHoldingId, duplicateIds,
//      orphanIds, invalidEntries, conflicts: [{ companyId, holdingIds, otherOperatorHoldings }] } }]
// Los strings pueden venir en null. Solo `conflicts` bloquea el cobro del holding.
export const listOperatorHoldings = () => Axios.get(HOLDINGS);

// Body { activity, fiscalAddress: { street, municipality, state, city?, country? } }: todos
// los strings recortados y no vacíos; cualquier otro campo (también anidado) → 400. `city` y
// `country` ausentes conservan lo guardado. Responde { id, name, legalName,
// businessIdentifier, activity, fiscalAddress, missingFiscalFields }.
// 400 invalid_holding_id · 404 holding_not_found (no existe o es de otro operador).
export const updateHoldingFiscalProfile = (holdingId, body) =>
  Axios.patch(`${HOLDINGS}/${encodeURIComponent(holdingId)}/fiscal-profile`, body);

// Vincula una empresa al holding (company.holdingId + holding.companies[]). Idempotente:
// 200 { holdingId, companyId, companyUpdated, listUpdated }. Solo ADMIN y LOGISTIC_SUPERADMIN:
// 403 forbidden_role para soporte y supervisor.
// 404 holding_not_found | company_not_found (sin contrato ACTIVO con el operador) ·
// 409 company_in_other_holding { holdingIds } | company_multi_operator (contrato activo con
// otro operador) | membership_changed (reintentar).
export const linkHoldingCompany = (holdingId, companyId) =>
  Axios.post(`${HOLDINGS}/${encodeURIComponent(holdingId)}/companies`, { companyId });

// Crea una empresa NUEVA en el holding, con el contrato replicado (el activo y vigente más
// reciente de las empresas del holding con el operador, con inicio y firma hoy), su ficha en
// companiesintegrations y su lugar en el holding, en una sola transacción. Solo ADMIN y
// LOGISTIC_SUPERADMIN (403 forbidden_role para el resto).
// Body { name, legalName, fiscalAddress, shippingAddresses: [{ address, type, municipality,
//   state, contactPhone, lat?, lng? }], contacts: { primary: { name, email, phone, role? },
//   billing?: {…, rut?} }, pickUpTime: 'HH:mm', workingDays?, ff?, operationsCountry?,
//   businessIdentifier? (RUT propio; sin él, el correlativo del holding), externalId?,
//   integration?: { integrationName, sender_id, alias? } }. Cualquier otro campo → 400.
// 201 { holdingId, companyId, name, businessIdentifier, externalId, contractId,
//   templateContractId, senderIds, created } · 200 igual si `externalId` ya existía.
// 404 holding_not_found · 409 template_contract_missing | holding_without_operator |
// sender_id_taken | tax_id_taken (reintentar si fue el correlativo) | holding_without_tax_id ·
// 400 integration_not_allowed | invalid_tax_id | operations_country_required.
export const createHoldingChildCompany = (holdingId, body) =>
  Axios.post(`${HOLDINGS}/${encodeURIComponent(holdingId)}/child-companies`, body);

// ─── Credenciales de la API de holdings (microservice-integrations) ─────────────
// Con ellas el sistema del holding crea empresas por API (scope `companies:write`). Las
// administra el operador (ADMIN y LOGISTIC_SUPERADMIN); integrations verifica contra
// contracts que el holding sea del operador del token (404 si no). El `clientSecret` viaja
// UNA sola vez, en la respuesta de crear o de rotar.
// Mismo contrato que las del comercio (api/integrations.js): `{ data }` o `{ error, status }`.
const holdingCredentialsCall = async (request) => {
  try {
    const response = await request();
    return { data: response.data };
  } catch (err) {
    return { error: err?.response?.data, status: err?.response?.status };
  }
};

const credentialsPath = (holdingId) =>
  `integrations/holdings/${encodeURIComponent(holdingId)}/api-credentials`;

export const holdingApiCredentials = (holdingId) => ({
  list: () => holdingCredentialsCall(() => Axios.get(credentialsPath(holdingId))),
  create: (values) =>
    holdingCredentialsCall(() => Axios.post(credentialsPath(holdingId), values)),
  rotate: (clientId) =>
    holdingCredentialsCall(() =>
      Axios.post(`${credentialsPath(holdingId)}/${encodeURIComponent(clientId)}/rotate`),
    ),
  revoke: (clientId) =>
    holdingCredentialsCall(() =>
      Axios.delete(`${credentialsPath(holdingId)}/${encodeURIComponent(clientId)}`),
    ),
});
